import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { offsetTypes } from '../../../../../../../../../../shared/core/shared.selectors';
import { DailyReportService } from '../../../../daily-report.service';
import { projectDetails } from '../../../../../../../../core/projects.selectors';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../../shared/components';
import { StationRangeMapGeometryService } from '../../../../../../../../../../shared/services/station-range-map-geometry.service';

@Component({
  standalone: false,
  selector: 'app-item-postings-station-popup',
  templateUrl: './item-postings-station-popup.component.html',
  styleUrls: ['./item-postings-station-popup.component.scss'],
})
export class ItemPostingsStationPopupComponent implements OnInit, OnDestroy {
  form: FormGroup = new FormGroup({});
  offsetTypeOptions: any[] = [];
  alignmentOptions: any[] = [];
  stationOptions: any[] = [];
  filteredFromStationOptions: any[] = [];
  filteredToStationOptions: any[] = [];
  stationRangeMapData: any = null;
  hasAlignmentGeometry = false;
  private alignments: any[] = [];
  private initialStationRange: any = null;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public dialogRef: MatDialogRef<ItemPostingsStationPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<fromRoot.State>,
    private dailyReportService: DailyReportService,
    private dialog: MatDialog,
    private geometryService: StationRangeMapGeometryService,
  ) {
    if (data?.form) {
      this.form = data.form;
    }
    this.initialStationRange = this.geometryService.normalizeStationRange(data?.stationRange);
  }

  ngOnInit() {
    this.store.select(offsetTypes)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.offsetTypeOptions = data;
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.alignments = this.geometryService.parseAlignmentGeometry(project?.alignment_geometry || project?.alignmentGeometry);
        this.hasAlignmentGeometry = this.alignments.length > 0;
        this.updateDialogSize();
        this.alignmentOptions = this.alignments.map(alignment => ({
          value: alignment.name,
          label: alignment.name,
        }));
        if (!this.form.controls?.alignment?.value && this.alignmentOptions.length) {
          this.form.controls.alignment?.setValue(this.alignmentOptions[0].value, { emitEvent: false });
        }
        this.hydrateFormFromInitialStationRange();
        this.updateStationOptions();
        this.updateFilteredStationOptions();
        this.updateStationRangeMapData();
      });

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.updateStationOptions();
        this.updateFilteredStationOptions();
        this.updateStationRangeMapData();
      });
  }

  private updateDialogSize() {
    this.dialogRef.updateSize(
      this.hasAlignmentGeometry ? '1200px' : '552px',
      this.hasAlignmentGeometry ? '620px' : 'auto',
    );
  }

  save() {
    this.dialogRef.close();

    const {
      stationRangeFromStation,
      stationRangeFromOffsetType,
      stationRangeFromOffsetDistance,
      stationRangeToStation,
      stationRangeToOffsetType,
      stationRangeToOffsetDistance,
      alignment,
    } = this.form.getRawValue();

    const stationRange = {
      alignment,
      from: {
        station: stationRangeFromStation,
        offsetType: stationRangeFromOffsetType,
        offsetDistance: stationRangeFromOffsetDistance,
      },
      to: {
        station: stationRangeToStation,
        offsetType: stationRangeToOffsetType,
        offsetDistance: stationRangeToOffsetDistance,
      },
    };

    this.dailyReportService.setStationRange.next({ stationRange, form: this.form });
  }

  checkStationFormData() {
    const values = this.form.getRawValue();
    return !!(
      values.stationRangeFromStation
      || values.stationRangeFromOffsetType
      || values.stationRangeFromOffsetDistance
      || values.stationRangeToStation
      || values.stationRangeToOffsetType
      || values.stationRangeToOffsetDistance
      || values.alignment
    );
  }

  close() {
    this.dialogRef.close();
  }

  private updateStationOptions() {
    const alignment = this.getSelectedAlignment();
    const options = (alignment?.stations || []).map(station => ({
      value: station.name,
      label: station.name,
    }));
    [
      this.form.controls?.stationRangeFromStation?.value,
      this.form.controls?.stationRangeToStation?.value,
    ].filter(Boolean).forEach(stationName => {
      if (!options.some(option => option.value === stationName)) {
        options.push({ value: stationName, label: stationName });
      }
    });
    this.stationOptions = options;
  }

  private updateFilteredStationOptions() {
    this.filteredFromStationOptions = this.getFilteredStationOptions('stationRangeFromStation');
    this.filteredToStationOptions = this.getFilteredStationOptions('stationRangeToStation');
  }

  private getFilteredStationOptions(controlName: string) {
    const value = `${this.form.controls?.[controlName]?.value || ''}`;
    const search = value.toLowerCase();
    const matches = search
      ? this.stationOptions.filter(option => `${option.label}`.toLowerCase().includes(search))
      : this.stationOptions;
    const limitedMatches = matches.slice(0, 25);
    if (value && !limitedMatches.some(option => option.value === value)) {
      return [{ value, label: value }, ...limitedMatches];
    }
    return limitedMatches;
  }

  private getSelectedAlignment() {
    const alignmentName = this.form.controls?.alignment?.value;
    return alignmentName
      ? this.alignments.find(alignment => alignment.name === alignmentName)
      : this.alignments[0];
  }

  private updateStationRangeMapData() {
    const alignment = this.getSelectedAlignment();
    if (!alignment?.polylineCoordinates?.length) {
      this.stationRangeMapData = null;
      return;
    }

    const fromStationName = this.form.controls?.stationRangeFromStation?.value;
    const toStationName = this.form.controls?.stationRangeToStation?.value;
    const fromOffsetType = this.form.controls?.stationRangeFromOffsetType?.value;
    const fromOffsetDistance = this.form.controls?.stationRangeFromOffsetDistance?.value;
    const toOffsetType = this.form.controls?.stationRangeToOffsetType?.value;
    const toOffsetDistance = this.form.controls?.stationRangeToOffsetDistance?.value;
    const selectedPath = this.geometryService.getStationRangePolyline({ alignment, fromStationName, toStationName });

    const fromStationPosition = this.geometryService.getStationPositionOnAlignment({ alignment, stationName: fromStationName })?.coordinate || null;
    const toStationPosition = this.geometryService.getStationPositionOnAlignment({ alignment, stationName: toStationName })?.coordinate || null;
    const fromOffsetPoint = this.hasNonZeroOffset(fromOffsetType, fromOffsetDistance)
      ? this.geometryService.getOffsetStationPosition({ alignment, stationName: fromStationName, offsetType: fromOffsetType, offsetDistance: fromOffsetDistance })
      : null;
    const toOffsetPoint = this.hasNonZeroOffset(toOffsetType, toOffsetDistance)
      ? this.geometryService.getOffsetStationPosition({ alignment, stationName: toStationName, offsetType: toOffsetType, offsetDistance: toOffsetDistance })
      : null;

    // Single-endpoint preview: exactly one station entered (mirrors mobile —
    // show the station point + its perpendicular offset line before TO is set).
    const hasRange = selectedPath.length >= 2;
    const singleStationPosition = !hasRange
      ? (fromStationPosition || toStationPosition)
      : null;
    const singleOffsetPoint = !hasRange
      ? (fromStationPosition ? fromOffsetPoint : toOffsetPoint)
      : null;

    let path;
    if (hasRange) {
      path = selectedPath;
    } else if (singleStationPosition) {
      path = [singleStationPosition, singleOffsetPoint].filter(Boolean);
    } else {
      path = alignment.polylineCoordinates;
    }

    this.stationRangeMapData = {
      center: this.geometryService.getMapCenter(path),
      geometry: this.buildLineStringFeatureCollection(alignment, selectedPath, fromOffsetPoint, toOffsetPoint, fromStationPosition, toStationPosition, singleStationPosition),
      selectedPath: hasRange ? path : [],
      endCapPaths: hasRange ? this.geometryService.getStationRangeEndCapPaths(path[0], path[path.length - 1]) : [],
      fromPosition: hasRange ? path[0] : fromStationPosition,
      toPosition: hasRange ? path[path.length - 1] : toStationPosition,
      fromOffsetPoint,
      toOffsetPoint,
      fromLabel: hasRange ? fromStationName : (fromStationPosition ? fromStationName : null),
      toLabel: hasRange ? toStationName : (toStationPosition ? toStationName : null),
      zoom: this.getStationRangeMapZoom(path),
    };
  }

  private hasNonZeroOffset(offsetType: any, offsetDistance: any) {
    const type = `${offsetType || ''}`.toUpperCase();
    return (type.startsWith('RT') || type.startsWith('LT')) && Number(offsetDistance) > 0;
  }

  selectAlignmentFromMap(point: any) {
    if (!this.alignments.length || !Number.isFinite(Number(point?.latitude)) || !Number.isFinite(Number(point?.longitude))) {
      return;
    }

    const currentAlignmentName = this.form.controls?.alignment?.value;
    const clickedAlignmentName = (point?.stationRangeAlignments || [])
      .find(alignmentName => alignmentName && alignmentName !== currentAlignmentName)
      || point?.stationRangeAlignment;

    if (clickedAlignmentName && clickedAlignmentName !== currentAlignmentName) {
      this.changeAlignment(clickedAlignmentName);
      return;
    }

    const clickedPoint = { lat: Number(point.latitude), lng: Number(point.longitude) };
    const alignmentDistances = this.alignments
      .filter(alignment => alignment.polylineCoordinates?.length >= 2)
      .map(alignment => ({
        alignment,
        distance: this.getDistanceToPolylineFeet(clickedPoint, alignment.polylineCoordinates),
      }))
      .sort((a, b) => a.distance - b.distance);
    const closestAlignment = alignmentDistances[0]?.alignment;

    if (closestAlignment?.name && closestAlignment.name !== this.form.controls?.alignment?.value) {
      this.changeAlignment(closestAlignment.name);
    }
  }

  private changeAlignment(alignmentName: string) {
    if (!alignmentName || alignmentName === this.form.controls?.alignment?.value) {
      return;
    }

    if (!this.hasStationRangeValues()) {
      this.form.controls?.alignment?.setValue(alignmentName);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Change alignment?',
        'Station values will be cleared.',
        'Change',
        'Cancel',
      ),
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe(result => {
        if (result) {
          this.form.patchValue({
            alignment: alignmentName,
            stationRangeFromStation: null,
            stationRangeFromOffsetType: null,
            stationRangeFromOffsetDistance: null,
            stationRangeToStation: null,
            stationRangeToOffsetType: null,
            stationRangeToOffsetDistance: null,
          });
        }
      });
  }

  private hasStationRangeValues() {
    const values = this.form.getRawValue();
    return !!(
      values.stationRangeFromStation
      || values.stationRangeFromOffsetType
      || values.stationRangeFromOffsetDistance
      || values.stationRangeToStation
      || values.stationRangeToOffsetType
      || values.stationRangeToOffsetDistance
    );
  }

  private hydrateFormFromInitialStationRange() {
    if (!this.initialStationRange) return;

    const values = this.form.getRawValue();
    const formHasStationRangeValues = !!(
      values.stationRangeFromStation
      || values.stationRangeFromOffsetType
      || values.stationRangeFromOffsetDistance
      || values.stationRangeToStation
      || values.stationRangeToOffsetType
      || values.stationRangeToOffsetDistance
    );

    if (formHasStationRangeValues) return;

    this.form.patchValue({
      alignment: this.initialStationRange.alignment ?? values.alignment,
      stationRangeFromStation: this.initialStationRange.from?.station ?? null,
      stationRangeFromOffsetType: this.initialStationRange.from?.offsetType ?? null,
      stationRangeFromOffsetDistance: this.initialStationRange.from?.offsetDistance ?? null,
      stationRangeToStation: this.initialStationRange.to?.station ?? null,
      stationRangeToOffsetType: this.initialStationRange.to?.offsetType ?? null,
      stationRangeToOffsetDistance: this.initialStationRange.to?.offsetDistance ?? null,
    }, { emitEvent: false });
  }

  private normalizeStationRange(stationRange: any) {
    if (!stationRange) return null;

    const from = stationRange.from || {};
    const to = stationRange.to || {};
    const normalized = {
      alignment: stationRange.alignment || null,
      from: {
        station: from.station || null,
        offsetType: from.offsetType ?? from.offset_type ?? null,
        offsetDistance: from.offsetDistance ?? from.offset_distance ?? null,
      },
      to: {
        station: to.station || null,
        offsetType: to.offsetType ?? to.offset_type ?? null,
        offsetDistance: to.offsetDistance ?? to.offset_distance ?? null,
      },
    };

    const hasValue = [
      normalized.alignment,
      normalized.from.station,
      normalized.from.offsetType,
      normalized.from.offsetDistance,
      normalized.to.station,
      normalized.to.offsetType,
      normalized.to.offsetDistance,
    ].some(value => value !== null && value !== undefined && value !== '');

    return hasValue ? normalized : null;
  }

  private parseStationToFeet(stationName: string): number | null {
    const match = `${stationName || ''}`.match(/^(\d+)\+(\d+)$/);
    if (!match) return null;
    return parseInt(match[1], 10) * 100 + parseInt(match[2], 10);
  }

  private getOffsetDirectionMultiplier(offsetType: string) {
    if (`${offsetType || ''}`.startsWith('RT')) return -1;
    if (`${offsetType || ''}`.startsWith('LT')) return 1;
    return 0;
  }

  private getSignedOffsetFeet({ offsetType, offsetDistance }) {
    const distance = parseFloat(offsetDistance);
    return this.geometryService.getOffsetDirectionMultiplier(offsetType) * (Number.isFinite(distance) ? distance : 0);
  }

  private getDistanceBetweenCoordinatesFeet(start: any, end: any) {
    if (!start || !end) return 0;
    const earthRadiusFeet = 20902231;
    const toRad = deg => (deg * Math.PI) / 180;
    const latitude = (start.lat + end.lat) / 2;
    const cosLat = Math.max(Math.cos(toRad(latitude)), 0.000001);
    const dx = toRad(end.lng - start.lng) * earthRadiusFeet * cosLat;
    const dy = toRad(end.lat - start.lat) * earthRadiusFeet;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private getDistanceToPolylineFeet(point: any, polyline: any[]) {
    if (!point || !polyline?.length) return Number.POSITIVE_INFINITY;

    let closestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index < polyline.length - 1; index++) {
      closestDistance = Math.min(
        closestDistance,
        this.getDistanceToSegmentFeet(point, polyline[index], polyline[index + 1]),
      );
    }
    return closestDistance;
  }

  private getDistanceToSegmentFeet(point: any, start: any, end: any) {
    if (!point || !start || !end) return Number.POSITIVE_INFINITY;

    const latitude = (point.lat + start.lat + end.lat) / 3;
    const earthRadiusFeet = 20902231;
    const toRad = deg => (deg * Math.PI) / 180;
    const cosLat = Math.max(Math.cos(toRad(latitude)), 0.000001);
    const toXY = coordinate => ({
      x: toRad(coordinate.lng) * earthRadiusFeet * cosLat,
      y: toRad(coordinate.lat) * earthRadiusFeet,
    });
    const p = toXY(point);
    const a = toXY(start);
    const b = toXY(end);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSquared = dx * dx + dy * dy;
    const t = lengthSquared ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared)) : 0;
    const projected = {
      x: a.x + t * dx,
      y: a.y + t * dy,
    };
    return Math.sqrt((p.x - projected.x) ** 2 + (p.y - projected.y) ** 2);
  }

  private getOffsetCoordinate({ coordinate, previousCoordinate, nextCoordinate, offsetFeet }) {
    if (!coordinate || !offsetFeet) return coordinate;
    const tangentStart = previousCoordinate || coordinate;
    const tangentEnd = nextCoordinate || coordinate;
    const earthRadiusFeet = 20902231;
    const toRad = deg => (deg * Math.PI) / 180;
    const toDeg = rad => (rad * 180) / Math.PI;
    const cosLat = Math.max(Math.cos(toRad(coordinate.lat)), 0.000001);
    const dx = toRad(tangentEnd.lng - tangentStart.lng) * earthRadiusFeet * cosLat;
    const dy = toRad(tangentEnd.lat - tangentStart.lat) * earthRadiusFeet;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (!length) return coordinate;

    const leftNormalX = -dy / length;
    const leftNormalY = dx / length;
    const offsetX = leftNormalX * offsetFeet;
    const offsetY = leftNormalY * offsetFeet;

    return {
      lat: coordinate.lat + toDeg(offsetY / earthRadiusFeet),
      lng: coordinate.lng + toDeg(offsetX / (earthRadiusFeet * cosLat)),
    };
  }

  private getOffsetStationRangePolyline({
    polyline,
    fromOffsetType,
    fromOffsetDistance,
    toOffsetType,
    toOffsetDistance,
  }) {
    if (!polyline?.length || polyline.length < 2) return polyline || [];

    const fromOffsetFeet = this.geometryService.getSignedOffsetFeet({ offsetType: fromOffsetType, offsetDistance: fromOffsetDistance });
    const toOffsetFeet = this.geometryService.getSignedOffsetFeet({ offsetType: toOffsetType, offsetDistance: toOffsetDistance });
    if (!fromOffsetFeet && !toOffsetFeet) return polyline;

    const segmentLengths = polyline
      .slice(0, -1)
      .map((coordinate, index) => this.getDistanceBetweenCoordinatesFeet(coordinate, polyline[index + 1]));
    const totalLength = segmentLengths.reduce((sum, length) => sum + length, 0);
    let cumulativeLength = 0;

    return polyline.map((coordinate, index) => {
      if (index > 0) {
        cumulativeLength += segmentLengths[index - 1] || 0;
      }

      const progress = totalLength ? cumulativeLength / totalLength : 0;
      const offsetFeet = fromOffsetFeet + (toOffsetFeet - fromOffsetFeet) * progress;
      return this.geometryService.getOffsetCoordinate({
        coordinate,
        previousCoordinate: polyline[index - 1],
        nextCoordinate: polyline[index + 1],
        offsetFeet,
      });
    });
  }

  private getStationPositionOnAlignment({ alignment, stationName }) {
    const stationFeet = this.geometryService.parseStationToFeet(stationName);
    const stations = alignment?.stations || [];
    if (stationFeet == null || stations.length < 2) return null;

    for (let index = 0; index < stations.length - 1; index++) {
      const segmentStartFeet = this.geometryService.parseStationToFeet(stations[index].name);
      const segmentEndFeet = this.geometryService.parseStationToFeet(stations[index + 1].name);
      const segmentStart = stations[index].coordinate;
      const segmentEnd = stations[index + 1].coordinate;

      if (segmentStartFeet == null || segmentEndFeet == null || !segmentStart || !segmentEnd) {
        continue;
      }

      const segmentLow = Math.min(segmentStartFeet, segmentEndFeet);
      const segmentHigh = Math.max(segmentStartFeet, segmentEndFeet);
      if (stationFeet < segmentLow || stationFeet > segmentHigh) {
        continue;
      }

      const ratio = segmentEndFeet === segmentStartFeet
        ? 0
        : (stationFeet - segmentStartFeet) / (segmentEndFeet - segmentStartFeet);

      return {
        coordinate: {
          lat: segmentStart.lat + ratio * (segmentEnd.lat - segmentStart.lat),
          lng: segmentStart.lng + ratio * (segmentEnd.lng - segmentStart.lng),
        },
        previousCoordinate: segmentStart,
        nextCoordinate: segmentEnd,
      };
    }

    return null;
  }

  private getOffsetStationPosition({ alignment, stationName, offsetType, offsetDistance }) {
    const stationPosition = this.geometryService.getStationPositionOnAlignment({ alignment, stationName });
    if (!stationPosition) return null;

    return this.geometryService.getOffsetCoordinate({
      coordinate: stationPosition.coordinate,
      previousCoordinate: stationPosition.previousCoordinate,
      nextCoordinate: stationPosition.nextCoordinate,
      offsetFeet: this.geometryService.getSignedOffsetFeet({ offsetType, offsetDistance }),
    });
  }

  private makeCoordinate(coordinate: any) {
    if (!coordinate) return null;
    if (Array.isArray(coordinate) && coordinate.length >= 2) {
      return { lat: Number(coordinate[1]), lng: Number(coordinate[0]) };
    }
    const lat = Number(coordinate.lat ?? coordinate.latitude);
    const lng = Number(coordinate.lng ?? coordinate.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
    return null;
  }

  private parseAlignmentGeometry(alignmentGeometry: any) {
    try {
      const geojson = typeof alignmentGeometry === 'string'
        ? JSON.parse(alignmentGeometry)
        : alignmentGeometry;
      if (!geojson?.features?.length) return [];

      return geojson.features.map(feature => {
        const coordinates = feature?.geometry?.coordinates || [];
        const rawStations = feature?.properties?.stations || [];
        const stations = rawStations.map((station, index) => {
          const coordinate = this.geometryService.makeCoordinate(coordinates[index]);
          return coordinate ? { name: station?.name, coordinate } : null;
        }).filter(Boolean);

        return {
          name: feature?.properties?.name || '',
          polylineCoordinates: coordinates.map(coordinate => this.geometryService.makeCoordinate(coordinate)).filter(Boolean),
          stations,
        };
      });
    } catch {
      return [];
    }
  }

  private getStationRangePolyline({ alignment, fromStationName, toStationName }) {
    const fromFeet = this.geometryService.parseStationToFeet(fromStationName);
    const toFeet = this.geometryService.parseStationToFeet(toStationName);
    const stations = alignment?.stations || [];

    if (fromFeet == null || toFeet == null || stations.length < 2 || !alignment?.polylineCoordinates?.length) {
      return [];
    }

    const rangeStart = Math.min(fromFeet, toFeet);
    const rangeEnd = Math.max(fromFeet, toFeet);
    const points = [];

    for (let index = 0; index < stations.length - 1; index++) {
      const segmentStartFeet = this.geometryService.parseStationToFeet(stations[index].name);
      const segmentEndFeet = this.geometryService.parseStationToFeet(stations[index + 1].name);
      const segmentStart = stations[index].coordinate;
      const segmentEnd = stations[index + 1].coordinate;

      if (segmentStartFeet == null || segmentEndFeet == null || !segmentStart || !segmentEnd) {
        continue;
      }

      const segmentLow = Math.min(segmentStartFeet, segmentEndFeet);
      const segmentHigh = Math.max(segmentStartFeet, segmentEndFeet);
      const overlapStart = Math.max(rangeStart, segmentLow);
      const overlapEnd = Math.min(rangeEnd, segmentHigh);

      if (overlapStart > overlapEnd) {
        continue;
      }

      const coordinateForFeet = feet => {
        const ratio = segmentEndFeet === segmentStartFeet
          ? 0
          : (feet - segmentStartFeet) / (segmentEndFeet - segmentStartFeet);
        return {
          lat: segmentStart.lat + ratio * (segmentEnd.lat - segmentStart.lat),
          lng: segmentStart.lng + ratio * (segmentEnd.lng - segmentStart.lng),
        };
      };

      const startCoordinate = coordinateForFeet(overlapStart);
      const endCoordinate = coordinateForFeet(overlapEnd);
      const lastPoint = points[points.length - 1];

      if (!lastPoint || lastPoint.lat !== startCoordinate.lat || lastPoint.lng !== startCoordinate.lng) {
        points.push(startCoordinate);
      }
      points.push(endCoordinate);
    }

    return points;
  }

  private getMapCenter(points: any[]) {
    const totals = points.reduce((acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }), { lat: 0, lng: 0 });

    return {
      lat: totals.lat / points.length,
      lng: totals.lng / points.length,
    };
  }

  private getStationRangeMapZoom(points: any[]) {
    const bounds = points.reduce((acc, point) => ({
      minLat: Math.min(acc.minLat, point.lat),
      maxLat: Math.max(acc.maxLat, point.lat),
      minLng: Math.min(acc.minLng, point.lng),
      maxLng: Math.max(acc.maxLng, point.lng),
    }), {
      minLat: points[0].lat,
      maxLat: points[0].lat,
      minLng: points[0].lng,
      maxLng: points[0].lng,
    });
    const span = Math.max(bounds.maxLat - bounds.minLat, bounds.maxLng - bounds.minLng);

    // Single point with no offset span: use a context zoom, not max.
    if (points.length < 2 || span === 0) return 16;
    if (span < 0.0003) return 19;
    if (span < 0.0008) return 18;
    return 17;
  }

  private getStationRangeEndCapPaths(start: any, end: any) {
    if (!start || !end) return [];

    const latDelta = end.lat - start.lat;
    const lngDelta = end.lng - start.lng;
    const length = Math.sqrt(latDelta * latDelta + lngDelta * lngDelta);
    if (!length) return [];

    const perpendicularLat = -lngDelta / length;
    const perpendicularLng = latDelta / length;
    const capLength = Math.max(length * 0.16, 0.000055);
    const halfCapLength = capLength / 2;

    const capForPoint = point => [
      {
        lat: point.lat - perpendicularLat * halfCapLength,
        lng: point.lng - perpendicularLng * halfCapLength,
      },
      {
        lat: point.lat + perpendicularLat * halfCapLength,
        lng: point.lng + perpendicularLng * halfCapLength,
      },
    ];

    return [capForPoint(start), capForPoint(end)];
  }

  private buildLineStringFeatureCollection(selectedAlignment: any, selectedPath: any[] = [], fromOffsetPoint: any = null, toOffsetPoint: any = null, fromStationPosition: any = null, toStationPosition: any = null, singleStationPosition: any = null) {
    const features = [];
    // Extent strategy:
    //  - FROM only, no offset  → keep the whole alignment in view (context)
    //  - FROM only, with offset → moderate zoom around station+offset
    //  - full FROM→TO range     → zoom to the range extent
    const hasSingleOffset = !!(singleStationPosition && (fromOffsetPoint || toOffsetPoint));
    const fitWholeAlignment = selectedPath?.length < 2 && !hasSingleOffset;
    this.alignments
      .filter(alignment => alignment.polylineCoordinates?.length >= 2)
      .forEach(alignment => {
        const isSelectedAlignment = alignment.name === selectedAlignment?.name;
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: alignment.polylineCoordinates.map(point => [point.lng, point.lat]),
            color: isSelectedAlignment ? (selectedPath?.length >= 2 ? '#8EA0D8' : '#1036D6') : '#1036D6',
            opacity: isSelectedAlignment ? 1 : 0.35,
            strokeWidth: isSelectedAlignment ? 4 : 3,
            includeInExtent: fitWholeAlignment,
            station_range_alignment: alignment.name,
            station_range_part: isSelectedAlignment ? 'selected_alignment' : 'alignment',
          },
        });
      });

    if (!features.length && selectedAlignment?.polylineCoordinates?.length >= 2) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: selectedAlignment.polylineCoordinates.map(point => [point.lng, point.lat]),
          color: selectedPath?.length >= 2 ? '#8EA0D8' : '#1036D6',
          opacity: selectedPath?.length >= 2 ? 0.55 : 1,
          strokeWidth: selectedPath?.length >= 2 ? 2 : 3,
          includeInExtent: fitWholeAlignment,
          station_range_alignment: selectedAlignment.name,
          station_range_part: 'selected_alignment',
        },
      });
    }

    // Single-endpoint preview: draw a dot at the station position so the user
    // sees the located point before entering the TO station.
    // Single-endpoint preview with no offset yet: show the hollow ring dot at
    // the station (on the in-context alignment, not zoomed). Once an offset is
    // entered, the ring moves to the offset (drawn by the offset block below).
    if (singleStationPosition && !fromOffsetPoint && !toOffsetPoint) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [singleStationPosition.lng, singleStationPosition.lat],
          color: '#1036D6',
          markerIconUrl: '/assets/icons/map/markers/offset-ring.svg',
          iconSize: { width: 10, height: 10, yOffset: 5 },
          includeInExtent: false,
        },
      });
    }

    // Single station WITH offset: pad the extent (~±0.0004°) so the map zooms
    // in only moderately — offset tie line clearly visible, but not a tight
    // street-level fit. Use an invisible line (opacity 0) rather than points so
    // nothing extra is drawn; only its endpoints drive the extent.
    if (hasSingleOffset) {
      const pad = 0.0004;
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [
            [singleStationPosition.lng - pad, singleStationPosition.lat - pad],
            [singleStationPosition.lng + pad, singleStationPosition.lat + pad],
          ],
          color: '#1036D6',
          opacity: 0,
          strokeWidth: 0,
          includeInExtent: true,
        },
      });
    }

    if (selectedPath?.length >= 2) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: selectedPath.map(point => [point.lng, point.lat]),
          color: '#1036D6',
          lineStyle: 'short-dash',
          strokeWidth: 4,
        },
      });

      const capStart = selectedPath[0];
      const capEnd = selectedPath[selectedPath.length - 1];
      // Skip the book-end cap on an end that has an offset — the offset tie
      // line + ring already mark that end (otherwise a small offset dot lands
      // inside the cap). path[0]/path[last] follow stationing, which may be
      // reversed vs FROM/TO, so match each cap to its nearest station.
      const endpointHasOffset = (coord: any) => {
        const dist = (a: any, b: any) => (a && b) ? Math.hypot(coord.lat - b.lat, coord.lng - b.lng) : Infinity;
        const dFrom = dist(coord, fromStationPosition);
        const dTo = dist(coord, toStationPosition);
        return dFrom <= dTo ? !!fromOffsetPoint : !!toOffsetPoint;
      };
      const endCaps = this.geometryService.getStationRangeEndCapPaths(capStart, capEnd);
      [[capStart, endCaps[0]], [capEnd, endCaps[1]]]
        .filter(([coord, capPath]) => capPath && !endpointHasOffset(coord))
        .map(([, capPath]) => capPath)
        .forEach(capPath => {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: capPath.map(point => [point.lng, point.lat]),
            color: '#1036D6',
            strokeWidth: 4,
          },
        });
      });

    }

    [
      { endpoint: fromStationPosition, offset: fromOffsetPoint },
      { endpoint: toStationPosition, offset: toOffsetPoint },
    ].forEach(({ endpoint, offset }) => {
      if (!endpoint || !offset) return;
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [[endpoint.lng, endpoint.lat], [offset.lng, offset.lat]],
          color: '#1036D6',
          strokeWidth: 1.5,
        },
      });
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [offset.lng, offset.lat],
          color: '#1036D6',
          markerIconUrl: '/assets/icons/map/markers/offset-ring.svg',
          iconSize: { width: 10, height: 10, yOffset: 5 },
          // Only drive the extent for a full range; a single station keeps the
          // whole alignment in view.
          includeInExtent: !singleStationPosition,
        },
      });
    });

    this.alignments
      .filter(alignment => alignment.name !== selectedAlignment?.name && alignment.polylineCoordinates?.length >= 2)
      .forEach(alignment => {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: alignment.polylineCoordinates.map(point => [point.lng, point.lat]),
            color: '#1036D6',
            opacity: 0.01,
            strokeWidth: 24,
            includeInExtent: false,
            station_range_alignment: alignment.name,
            station_range_part: 'alignment_hit_target',
          },
        });
      });

    return {
      type: 'FeatureCollection',
      features,
    };
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
